import {html, LitElement, nothing} from "lit";
import {customElement, query, state} from "lit/decorators.js";
import "../../common/controls-inputs/Button.js";
import "../../common/controls-inputs/Checkbox.js";
import "../../common/controls-inputs/LabeledTextInput.js";
import {renderPowerNotesLogo} from "../../common/powerNotesLogo.js";
import Data from "../../../main/data/index.js";
import {validate, emailValidators, passwordValidators} from "../../../main/utils/form.js";
import {Modal, MODALS} from "../../common/modals.js";
import {modalStyles} from "../../common/styles/Modals.js";
import Integration from "../../../integrations/integration/index.js";

const STATE = {
	LOGIN:      "login",
	FORGOT_PWD: "forgot-pwd",
	SENT_EMAIL: "sent-email"
};

@customElement("pn-int-login-modal")
export class IntegrationLoginModal extends LitElement {
	@state() email = "";
	@state() password = "";
	@state() state:       string = STATE.LOGIN;
	@state() error:       string | boolean = false;
	@state() fieldErrors: {email: boolean; password?: boolean} = {
		email:    false,
		password: false
	};

	@query(".modal-content") modalContent: HTMLElement;

	modal: Modal | null = null;

	static styles = [modalStyles];

	firstUpdated() {
		this.modal = new Modal({
			id:               "integration-login",
			modalContent:     this.modalContent,
			parent:           this.shadowRoot,
			isSingleUseModal: true
		});
		this.modal.show();
	}

	validateForm() {
		this.fieldErrors = {
			email:    validate(this.email, emailValidators),
			password: validate(this.password, passwordValidators)
		};

		return !!this.fieldErrors.email || !!this.fieldErrors.password;
	}

	onClickLogin = async () => {
		const hasErrors = this.validateForm();
		if (hasErrors)
			return;

		try {
			await Data.app.integrationLogin(this.email, this.password, Data.app._projectUuid);
			Integration.gtag("event", "login");
			this.error = false;
			this.modal?.hide();
		} catch (e) {
			this.error = e.message;
		}
	};

	onClickForgotPwd = async () => {
		// Validate email address
		const emailValid = validate(this.email, emailValidators);
		this.fieldErrors = {
			email: emailValid
		};
		if (emailValid !== undefined)
			return;

		try {
			await Data.app.backend.forgotPassword(this.email);
			this.error = false;
			this.state = STATE.SENT_EMAIL;
		} catch (e) {
			this.error = e.message;
		}
	};

	keydownOnForm = (event: KeyboardEvent) => {
		if (event.key === "Enter") {
			event.preventDefault();

			if (this.state === STATE.LOGIN)
				this.onClickLogin();
			else if (this.state === STATE.FORGOT_PWD)
				this.onClickForgotPwd();
		}
	};

	openSignupModal = () => {
		this.modal?.openNewModal(MODALS.INT_SIGNUP);
	};

	renderLoginForm() {
		return html`
			<div class="heading"><h2>${this.renderLogo()} Log In</h2></div>

			<div class="body">
				<p>Log in to save your work with PowerNotes.</p>
				<form>
					<pn-labeled-text-input
						autocomplete="email"
						.error=${this.fieldErrors.email}
						id="pn-login-email"
						inputType="email"
						label="Email"
						.required=${true}
						.onInput=${(e: {target: {value: string}}) => {
							this.email = e.target.value.trim();
						}}
						.onKeydown=${this.keydownOnForm}
						.value=${this.email}
					></pn-labeled-text-input>
					<pn-labeled-text-input
						autocomplete="off"
						.error=${this.fieldErrors.password}
						id="pn-login-pw"
						inputType="password"
						label="Password"
						.required=${true}
						.onInput=${(e: {target: {value: string}}) => {
							this.password = e.target.value.trim();
						}}
						.onKeydown=${this.keydownOnForm}
						.value=${this.password}
					></pn-labeled-text-input>
					<pn-button
						.onClick=${() => {
							this.error = false;
							this.state = STATE.FORGOT_PWD;
						}}
						renderType="link-blue"
					>Forgot password?</pn-button>
				</form>
			</div>

			<div class="buttons">
				<pn-button
					.onClick=${this.onClickLogin}
					renderType="auth"
				>Log In</pn-button>

				${this.error ? html`<div id="modal-error">
					<p class="modal-error-msg msg-error-with-bg">${this.error === true ? "Unknown error" : this.error}</p>
				</div>` : nothing}
			</div>

			<div class="footer">
				Need an account? <pn-button
					.onClick=${this.openSignupModal}
					renderType="link-blue"
				>Create account</pn-button>
			</div>
		`;
	}

	renderForgotPwdForm() {
		return html`
			<div class="heading"><h2>${this.renderLogo()} Forgot Password</h2></div>

			<div class="body">
				<form>
					<pn-labeled-text-input
						autocomplete="email"
						.error=${this.fieldErrors.email}
						id="pn-login-email"
						inputType="email"
						label="Email"
						.required=${true}
						.onInput=${(e: {target: {value: string}}) => {
							this.email = e.target.value.trim();
						}}
						.onKeydown=${this.keydownOnForm}
						.value=${this.email}
					></pn-labeled-text-input>
				</form>
			</div>

			<div class="buttons">
				<pn-button
					.onClick=${this.onClickForgotPwd}
					renderType="auth"
				>Submit</pn-button>

				${this.error ? html`<div id="modal-error">
					<p class="modal-error-msg msg-error-with-bg">${this.error === true ? "Unknown error" : this.error}</p>
				</div>` : nothing}

				<pn-button
					.onClick=${() => {
						this.error = false;
						this.state = STATE.LOGIN;
					}}
					renderType="link-blue"
				>Go back to Log In</pn-button>
			</div>
		`;
	}

	renderEmailSent() {
		return html`
			<div class="heading"><h2>${this.renderLogo()} Email Sent</h2></div>

			<div class="body">
				<p>If you have an account, you will receive an email with instructions shortly.</p>

				<pn-button
					.onClick=${() => {
						this.error = false;
						this.state = STATE.LOGIN;
					}}
					renderType="link-blue"
				>Go back to Log In</pn-button>
			</div>
		`;
	}

	renderContent() {
		switch (this.state) {
			case STATE.FORGOT_PWD:
				return this.renderForgotPwdForm();
			case STATE.SENT_EMAIL:
				return this.renderEmailSent();
			case STATE.LOGIN:
				return this.renderLoginForm();
		}
	}

	renderLogo() {
		return html`<div id="pn-modal-logo">${renderPowerNotesLogo()}<div>`;
	}

	render() {
		return html`
			<div class="modal-content">
				<div id="pn-modal-content">
					${this.renderContent()}
				</div>
			</div>
		`;
	}
};

declare global {
	interface HTMLElementTagNameMap {
		"pn-int-login-modal": IntegrationLoginModal;
	}
}
